/**
 * parallax.js — the layered city behind the fight.
 *
 * Replaces one flat photograph per screen with a three-layer stack drawn at
 * different rates. Two reasons, and the second is the one that matters:
 *
 * DEPTH. A single image is a painted wall. Layers that slide against each other
 * read as distance, and the game is set on a rooftop above a city — the depth is
 * the setting.
 *
 * SHAPE. A photograph fills a frame exactly as long as the frame is the shape
 * the photograph is. Widen the arena and it stretches; heighten it and it
 * stretches the other way. That is what made the playfield's proportions
 * effectively fixed. Layers have no such constraint: each covers the frame's
 * height and is cropped horizontally, so whatever space the play area does not
 * occupy simply shows more city. The arena can change shape without the art
 * fighting it.
 *
 * WHERE THE MOVEMENT COMES FROM. The game has no scrolling camera — each screen
 * is a fixed frame the fighters move around inside. So parallax is driven by two
 * things instead:
 *
 *   1. The screen index. Every screen offsets the layers further along the
 *      strip, so pushing an opponent across seven screens genuinely travels
 *      along one continuous skyline. That is literally what the match is, and it
 *      is why seven distinct-looking backdrops fall out of three images.
 *   2. A slow sway toward the midpoint between the fighters. Enough that the
 *      backdrop is alive during a duel, never enough to pull the eye off them.
 *
 * Everything here is a pure function of (screen, midpoint, frame size). No
 * state, nothing to sync — the online guest derives an identical backdrop from
 * the snapshot it already receives.
 */

import skyLayer from './assets/backgrounds/layer_sky.jpg';
import towerLayer from './assets/backgrounds/layer_towers.webp';
import midLayer from './assets/backgrounds/layer_mid.webp';
import nearLayer from './assets/backgrounds/layer_near.webp';

/**
 * The stack, furthest first.
 *
 * `depth` is how fast each moves relative to the play plane: 0 is infinitely
 * distant and never moves, 1 would track the fighters exactly. The gaps between
 * these numbers ARE the sense of depth — evenly spaced values read as flat
 * cards, so they widen toward the camera the way real distance does.
 *
 * Every layer is drawn at the SAME size, covering the frame height. Scaling the
 * nearer ones down would be the intuitive way to place them and it is wrong
 * twice over: it makes distant things larger than near ones, and it narrows
 * exactly the layer that has to pan furthest. Depth is carried by rate of
 * movement and by how each layer was composed — sky fills the frame, skyline
 * sits in the lower two thirds, clutter in the lower third.
 *
 */
const LAYERS = [
  // `size` is the layer's drawn height as a multiple of the PLAY area's height,
  // and `sit` is how far its bottom edge falls below the roofline, in the same
  // units. Both are relative to the play area rather than to the frame, and
  // that is the whole trick: the city then stays exactly the same size whatever
  // shape the screen is, and a taller frame reveals more SKY instead of
  // stretching the skyline to fit. Anchored to the frame instead, a phone held
  // upright squeezed the entire city into a band and left the rest empty.
  //
  // `tiled` layers carry writing (billboards, stencils), so they repeat rather
  // than mirror — a mirrored copy reads backwards. build_parallax.py cuts those
  // strips at street gaps and cross-fades the join so a plain repeat is
  // seamless; the flags there and here must agree.
  { src: skyLayer, depth: 0.06, size: 1.15, sit: 0.02, tiled: false },
  // Colossal spires that continue past the top of the frame. They exist for the
  // upper sky, which on a phone held upright is two thirds of the screen and
  // was previously an empty gradient — the fight had nothing above it. Sized to
  // rise from the roofline and run well off the top, so the frame crops them
  // rather than containing them, which is what makes them read as enormous.
  { src: towerLayer, depth: 0.03, size: 2.7, sit: 0.02, tiled: false },
  { src: midLayer, depth: 0.26, size: 0.72, sit: 0.03, tiled: true },
  { src: nearLayer, depth: 0.62, size: 0.5, sit: 0.09, tiled: true },
];

/**
 * How far along the strip each screen sits, in play-plane pixels.
 *
 * Now that the layers tile, there is no end to run off and this is free to be
 * a stride you can actually see: the near clutter moves ~285px per screen, so
 * each screen frames a genuinely different stretch of rooftop. It was a sixth
 * of this while the layers were single copies that had to stay in bounds.
 */
const SCREEN_STRIDE = 460;

/**
 * Peak sway toward the fighters' midpoint, in play-plane pixels. Deliberately
 * small: this is life, not a camera.
 */
const SWAY = 46;

/**
 * The sky layer's URL, for the CSS full-bleed backdrop behind the arena.
 *
 * That element fills whatever the 4:3 play area cannot reach on a wider or
 * taller screen. It uses the sky rather than the per-screen art it used to,
 * which is both cheaper — one constant instead of state tracked every frame —
 * and more correct: the bleed is the sky continuing past the frame, and the sky
 * is the one layer that does not change from screen to screen.
 */
export const BLEED_SOURCE = skyLayer;

// Loaded images, indexed alongside LAYERS.
const images = LAYERS.map(() => null);

/** Kick off loading. Call once at start; safe to repeat. */
export function preloadParallax() {
  LAYERS.forEach((layer, i) => {
    if (images[i]) return;
    const img = new Image();
    img.onload = () => { images[i] = img; };
    img.src = layer.src;
  });
}

/** @returns {boolean} whether every layer is ready to draw. */
export function parallaxReady() {
  return images.every(Boolean);
}

/**
 * How far along the skyline strip a view is, in play-plane px — the shift a
 * layer of depth 1 is drawn at. Each layer shifts by this times its depth, so
 * anything drawn in front of the stack (the lobby's deck) moves with the city
 * by using it too.
 *
 * @param {number} screen Screen index, fractional during a pan.
 * @param {number} midX   Point the view leans toward, in frame px.
 * @param {number} width  Frame width.
 * @returns {number}
 */
export function stripPosition(screen, midX, width) {
  // Sway as a fraction of the frame either side of centre, so it behaves the
  // same whatever shape the frame is.
  const sway = ((midX - width / 2) / (width / 2)) * SWAY;
  return screen * SCREEN_STRIDE + sway;
}

/**
 * Draw the backdrop.
 *
 * @param {CanvasRenderingContext2D} ctx
 * @param {number} screen  Screen index (0..6).
 * @param {number} midX    Midpoint between the fighters, in frame px.
 * @param {number} width   Frame width in canvas px.
 * @param {number} height  Frame height in canvas px.
 * @param {number} groundY The roofline, in frame coordinates. Everything is
 *   anchored to this rather than to the frame, so the horizon stays put however
 *   tall the frame becomes.
 * @param {number} playH   Height of the play area — the unit layer sizes are in.
 * @param {number} horizonY Eye level in frame coordinates. The sky's horizon
 *   glow is placed here so the city converges on the same line the containment
 *   cell's vanishing point sits on; with the two at different heights the box
 *   and the city read as seen from different places.
 * @param {number} now     Seconds, for the searchlights and the traffic.
 * @returns {boolean} false if the art is not loaded yet (caller falls back).
 */
export function drawParallax(ctx, screen, midX, width, height, groundY, playH, horizonY, now) {
  if (!parallaxReady()) return false;

  const along = stripPosition(screen, midX, width);

  // Layer geometry, all anchored to the roofline rather than to the frame.
  const place = (layer) => {
    const img = images[LAYERS.indexOf(layer)];
    const drawH = playH * layer.size;
    return {
      img,
      drawH,
      drawW: img.width * (drawH / img.height),
      y: groundY + playH * layer.sit - drawH,
    };
  };
  /**
   * Lay a layer across the whole frame, mirroring alternate copies.
   *
   * Sizing the layers against the PLAY area rather than the frame is what keeps
   * the city a constant size on every device — but it also means a layer can be
   * narrower than the frame it has to fill. The near clutter is only ~707px
   * drawn, against a 1298px landscape phone, so on its own it hugged the left
   * edge and left 591px of nothing to the right.
   *
   * Mirroring alternate copies rather than repeating them is what makes this
   * safe: a plain repeat butts the image's right edge against its own left
   * edge, and that discontinuity is a hard vertical seam sliding across the
   * skyline. A mirrored copy meets its neighbour on identical pixels, so there
   * is no seam to see — and with no seam there is no need to clamp the pan,
   * which in turn frees the per-screen stride to be large enough to actually
   * change the view. Layers with writing on them are the exception: they are
   * built as seamless tiles instead, and simply repeat.
   */
  const layAcross = (img, drawW, drawH, y, shift, tiled) => {
    // Index of the leftmost copy that is still on screen.
    const first = Math.floor(shift / drawW);
    for (let k = first; k * drawW - shift < width; k++) {
      const x = k * drawW - shift;
      // Every other copy is flipped, so adjacent edges always match.
      if (!tiled && ((k % 2) + 2) % 2 === 1) {
        ctx.save();
        ctx.translate(x + drawW, y);
        ctx.scale(-1, 1);
        ctx.drawImage(img, 0, 0, drawW, drawH);
        ctx.restore();
      } else {
        ctx.drawImage(img, x, y, drawW, drawH);
      }
    }
  };

  // Sky first, then the wash OVER it, fading out as it descends. Painting the
  // wash underneath instead leaves the image's hard top edge sitting in open
  // sky as a horizontal seam — and no amount of colour-matching hides it,
  // because the two are different gradients. Ending the wash IN the image means
  // there is no edge to hide.
  const sky = place(LAYERS[0]);
  layAcross(sky.img, sky.drawW, sky.drawH, sky.y, along * LAYERS[0].depth, LAYERS[0].tiled);

  const washEnd = Math.max(sky.y + playH * 0.45, 1);
  const wash = ctx.createLinearGradient(0, 0, 0, washEnd);
  wash.addColorStop(0, '#0a0a1c');
  wash.addColorStop(0.72, 'rgba(17, 13, 44, 0.72)');
  wash.addColorStop(1, 'rgba(20, 14, 52, 0)');
  ctx.fillStyle = wash;
  ctx.fillRect(0, 0, width, washEnd);

  // Everything else over the top. Their hard edges are meant to be hard — they
  // are buildings. The towers come first so the nearer skyline occludes them.
  //
  // The horizon haze is injected INTO the stack rather than painted over it:
  // after the distant towers, before the nearer skyline. That ordering is the
  // physical one — atmosphere veils what is behind it and not what is in front
  // — and it is what turns a flat wall of buildings into receding distance.
  //
  // The city's moving light goes in the same way, each at the depth it lives
  // at: searchlights and distant traffic behind the skyline (the buildings cut
  // their beams and swallow the cars), nearer traffic between the skyline and
  // the rooftop clutter.
  LAYERS.slice(1).forEach((layer, i) => {
    if (i === 1) {
      drawHorizonHaze(ctx, width, horizonY, playH);
      drawSearchlights(ctx, width, horizonY, playH, now, along);
      drawTraffic(ctx, width, horizonY, playH, now, along, FAR_LANES);
    }
    if (i === 2) drawTraffic(ctx, width, horizonY, playH, now, along, NEAR_LANES);
    const { img, drawW, drawH, y } = place(layer);
    layAcross(img, drawW, drawH, y, along * layer.depth, layer.tiled);
  });

  return true;
}

/**
 * Searchlights raking the sky from somewhere down in the city.
 *
 * Positions are fixed in the city (they pan with the skyline's depth, so each
 * screen of the push sees different ones); angles sweep slowly on their own
 * clocks. Additive light, drawn before the skyline so the buildings cut the
 * beams' bases off — a beam whose source is visible is a prop, one that rises
 * from behind the city is the city.
 *
 * @param {CanvasRenderingContext2D} ctx
 * @param {number} width    Frame width.
 * @param {number} horizonY Eye level, in frame coordinates.
 * @param {number} playH    Play-area height, the unit distances are in.
 * @param {number} now      Seconds.
 * @param {number} along    How far along the strip the view is (see drawParallax).
 */
function drawSearchlights(ctx, width, horizonY, playH, now, along) {
  const spacing = playH * 0.95;
  const shift = along * 0.2;
  const first = Math.floor((shift - playH) / spacing);
  ctx.save();
  ctx.globalCompositeOperation = 'lighter';
  for (let k = first; k * spacing - shift < width + playH; k++) {
    // Per-light constants from its index, so every light keeps its own
    // character whichever screen it is seen from.
    const seed = Math.sin(k * 12.9898) * 43758.5453;
    const rand = seed - Math.floor(seed);
    const baseX = k * spacing - shift + rand * spacing * 0.5;
    const baseY = horizonY + playH * 0.12;
    const angle = (rand - 0.5) * 0.7 + Math.sin(now * (0.18 + rand * 0.14) + k) * 0.32;
    const length = playH * 1.5;
    const spread = playH * (0.05 + rand * 0.04);

    ctx.save();
    ctx.translate(baseX, baseY);
    ctx.rotate(angle);
    const beam = ctx.createLinearGradient(0, 0, 0, -length);
    // The base is behind the skyline, so the light has to hold up into the
    // open sky above it — that is the only part anyone sees.
    beam.addColorStop(0, `rgba(170, 190, 255, ${0.18 + rand * 0.06})`);
    beam.addColorStop(0.5, `rgba(160, 180, 255, ${0.1 + rand * 0.04})`);
    beam.addColorStop(1, 'rgba(150, 170, 255, 0)');
    ctx.fillStyle = beam;
    ctx.beginPath();
    ctx.moveTo(-2, 0);
    ctx.lineTo(-spread, -length);
    ctx.lineTo(spread, -length);
    ctx.lineTo(2, 0);
    ctx.closePath();
    ctx.fill();
    ctx.restore();
  }
  ctx.restore();
}

/**
 * Air lanes. Each is a height (in play-heights above the horizon), a speed, a
 * direction, a size and a light colour; a lane carries a craft every `gap`
 * play-heights. Far lanes are small, slow and hazed; near lanes large and fast
 * — the same rule the layers follow.
 */
const FAR_LANES = [
  // Above the skyline's roofs and among its spires: anything lower is behind
  // the buildings, which is where it would be — and so never seen.
  { y: 0.62, speed: 0.08, dir: 1, size: 0.5, gap: 1.3, rgb: '120, 220, 255', alpha: 0.6 },
  { y: 0.74, speed: 0.06, dir: -1, size: 0.42, gap: 1.7, rgb: '255, 120, 200', alpha: 0.5 },
  { y: 0.86, speed: 0.1, dir: 1, size: 0.46, gap: 1.5, rgb: '255, 190, 110', alpha: 0.55 },
];
const NEAR_LANES = [
  { y: 0.34, speed: 0.24, dir: -1, size: 1, gap: 1.9, rgb: '120, 220, 255', alpha: 0.85 },
  { y: 0.5, speed: 0.18, dir: 1, size: 0.85, gap: 2.3, rgb: '255, 90, 140', alpha: 0.8 },
];

/**
 * Flying traffic: small dark craft with a headlight, a tail light and a light
 * trail, crossing the frame on fixed lanes. Pure function of time and view —
 * nothing to store and nothing for the online guest to be sent.
 *
 * @param {CanvasRenderingContext2D} ctx
 * @param {number} width    Frame width.
 * @param {number} horizonY Eye level, in frame coordinates.
 * @param {number} playH    Play-area height, the unit distances are in.
 * @param {number} now      Seconds.
 * @param {number} along    How far along the strip the view is.
 * @param {object[]} lanes  FAR_LANES or NEAR_LANES.
 */
function drawTraffic(ctx, width, horizonY, playH, now, along, lanes) {
  lanes.forEach((lane, li) => {
    const gap = playH * lane.gap;
    const len = playH * 0.028 * lane.size;
    const trail = len * 7;
    // Travel plus the lane's share of the parallax pan, wrapped onto a loop
    // one frame wider than the screen so craft enter and leave off-frame.
    const loop = width + gap;
    const travel = now * playH * lane.speed * lane.dir - along * lane.size * 0.3;
    const y = horizonY - playH * lane.y;
    for (let x0 = ((travel + li * 97) % gap + gap) % gap - gap; x0 < loop; x0 += gap) {
      const x = x0;
      if (x < -trail || x > width + trail) continue;
      const back = -lane.dir;

      ctx.save();
      ctx.globalCompositeOperation = 'lighter';
      const streak = ctx.createLinearGradient(x, 0, x + back * trail, 0);
      streak.addColorStop(0, `rgba(${lane.rgb}, ${lane.alpha * 0.5})`);
      streak.addColorStop(1, `rgba(${lane.rgb}, 0)`);
      ctx.fillStyle = streak;
      ctx.fillRect(Math.min(x, x + back * trail), y - len * 0.12, trail, len * 0.24);
      ctx.restore();

      // Hull, then its lights: white ahead, red behind.
      ctx.fillStyle = 'rgba(10, 12, 24, 0.9)';
      ctx.fillRect(x - len / 2, y - len * 0.22, len, len * 0.36);
      ctx.fillStyle = `rgba(255, 245, 225, ${lane.alpha})`;
      ctx.fillRect(x + lane.dir * len / 2 - 1, y - len * 0.1, 2, 2);
      ctx.fillStyle = `rgba(255, 70, 90, ${lane.alpha})`;
      ctx.fillRect(x - lane.dir * len / 2 - 1, y - len * 0.1, 2, 2);
    }
  });
}

/**
 * Light gathered at eye level, where the city recedes out of sight.
 *
 * This is the backdrop's half of a single shared perspective. The containment
 * cell converges on HORIZON_Y; without a matching horizon in the city the two
 * halves of the image claimed different eye heights — the box seen from above
 * the fighters, the city seen from down at deck level — and that disagreement
 * is visible long before it is nameable.
 *
 * Sized against the play area rather than in fixed pixels, so it stays the same
 * band of sky whatever the camera's zoom is doing.
 *
 * @param {CanvasRenderingContext2D} ctx
 * @param {number} width    Frame width.
 * @param {number} horizonY Eye level, in frame coordinates.
 * @param {number} playH    Play-area height, the unit distances are in.
 */
function drawHorizonHaze(ctx, width, horizonY, playH) {
  const above = playH * 0.30;
  const below = playH * 0.12;
  const haze = ctx.createLinearGradient(0, horizonY - above, 0, horizonY + below);
  haze.addColorStop(0, 'rgba(86, 62, 130, 0)');
  // Densest just above the line: haze thins with altitude, so the last clear
  // air is at eye level and everything below it is already looking through more
  // of the city's own glow.
  haze.addColorStop(0.78, 'rgba(122, 88, 168, 0.30)');
  haze.addColorStop(1, 'rgba(58, 42, 96, 0.16)');
  ctx.fillStyle = haze;
  ctx.fillRect(0, horizonY - above, width, above + below);
}

/**
 * Draw the rooftop the fight happens on.
 *
 * Conceptually the frontmost layer of the stack, and the reason it has to exist
 * at all: the layers behind it are a city that continues downward for ever, so
 * without an opaque deck the fighters read as standing on a line in mid-air
 * with buildings visible underneath them. The old flat backdrops each had a
 * rooftop painted into them and the shared ground shading only had to tint it;
 * a layered sky has no floor of its own.
 *
 * Opaque, therefore — not a wash. Where the player may stand has to be
 * unambiguous, and a translucent floor over a bright skyline is not.
 *
 * @param {CanvasRenderingContext2D} ctx
 * @param {number} groundY Floor line, in canvas px.
 * @param {number} width   Frame width.
 * @param {number} height  Frame height.
 * @param {number} horizonY Eye level, in frame coordinates. The deck is the one
 *   genuinely horizontal surface in the scene, so it is where perspective is
 *   most legible — its plating has to recede toward the same horizon the cell
 *   and the city use, or the floor reads as a flat card the fighters stand on.
 * @param {object} [seams] Plating seams running away from the viewer:
 *   `originX` is the frame x of world x=0 on the floor line and `step` the
 *   spacing in frame px, so the seams are fixed to the ROOF and slide under the
 *   camera like a floor rather than riding along with it.
 */
export function drawRooftop(ctx, groundY, width, height, horizonY, seams = null) {
  // Haze where the city meets the roof, so the skyline sits behind the deck
  // rather than being sliced off by it.
  const haze = ctx.createLinearGradient(0, groundY - 70, 0, groundY);
  haze.addColorStop(0, 'rgba(6, 8, 16, 0)');
  haze.addColorStop(1, 'rgba(6, 8, 16, 0.9)');
  ctx.fillStyle = haze;
  ctx.fillRect(0, groundY - 70, width, 70);

  // Wet dark metal: a touch of the sky's violet at the far edge, where the
  // surface is most grazing to the eye and so reflects the most sky.
  const slab = ctx.createLinearGradient(0, groundY, 0, height);
  slab.addColorStop(0, '#0e1022');
  slab.addColorStop(0.35, '#080a16');
  slab.addColorStop(1, '#04050b');
  ctx.fillStyle = slab;
  ctx.fillRect(0, groundY, width, height - groundY);

  // Seams running away from the viewer, converging on the vanishing point —
  // which is the frame's centre, because the vanishing point is the camera's.
  // With the cross-bands below they make a plated floor in perspective rather
  // than stripes painted on a wall.
  if (seams && seams.step > 4 && groundY > horizonY + 1) {
    const vpX = width / 2;
    const reach = (height - horizonY) / (groundY - horizonY);
    const firstK = Math.floor(-seams.originX / seams.step) - 2;
    ctx.strokeStyle = 'rgba(140, 170, 230, 0.05)';
    ctx.lineWidth = 1;
    ctx.beginPath();
    for (let k = firstK; k < firstK + 400; k++) {
      const x = seams.originX + k * seams.step;
      const xBottom = vpX + (x - vpX) * reach;
      if (x > width && xBottom > width) break;
      ctx.moveTo(x, groundY);
      ctx.lineTo(xBottom, height);
    }
    ctx.stroke();
  }

  // Deck plating: faint bands marking equal steps of real distance across the
  // roof, coming toward the viewer.
  //
  // Evenly spaced on screen — which is what these were — describes a wall, not
  // a floor. On a receding plane, equal steps of distance project to gaps that
  // GROW as they approach, and that widening is the entire cue that the surface
  // is lying down rather than standing up. `eye` is the drop from the horizon to
  // the deck's far edge, which sets the scale of the whole projection.
  const eye = Math.max(groundY - horizonY, 1);
  ctx.fillStyle = 'rgba(150, 180, 220, 0.035)';
  for (let i = 1; i <= 6; i++) {
    // Equal depth steps toward the camera; the 0.86 stops the nearest band
    // short of the singularity directly underfoot.
    const y = horizonY + eye / (1 - (i / 7) * 0.86);
    if (y >= height) break;
    ctx.fillRect(0, y, width, 1);
  }

  // Lit edge along the floor line — the single most important line on screen,
  // since it is where the fighters' feet are.
  ctx.fillStyle = 'rgba(130, 200, 255, 0.28)';
  ctx.fillRect(0, groundY - 1, width, 2);

  // Aerial perspective across the junction: a band of the city's own haze
  // lying over the deck's far edge, peaking exactly on the floor line. A
  // crisp lit rim on a uniformly dark slab is the anatomy of a floating
  // platform — an edge that bright says "surface ends here", and a deck as
  // dark at its far edge as underfoot has no depth of its own. Veiling the
  // rim, the farthest plating and the city bases in one gradient makes the
  // deck recede INTO the backdrop instead of hovering in front of it.
  const junction = ctx.createLinearGradient(0, groundY - 44, 0, groundY + 60);
  junction.addColorStop(0, 'rgba(100, 76, 150, 0)');
  junction.addColorStop(0.42, 'rgba(100, 76, 150, 0.17)');
  junction.addColorStop(1, 'rgba(100, 76, 150, 0)');
  ctx.fillStyle = junction;
  ctx.fillRect(0, groundY - 44, width, 104);
}

// Scratch canvas for the deck's reflection, reused every frame.
let gloss = null;

/**
 * The deck's gloss: the lit world just above the floor line, mirrored beneath
 * it and added as light.
 *
 * A wet roof at night is mostly reflection — the neon, the portals and the
 * boards' drive glow all land in it — and that is what makes the deck read as
 * a real surface the fighters stand ON rather than a dark band they stand in
 * front of. A planar mirror about the floor line is the cheap, standard
 * approximation, and it is exact for the thing that matters most: whatever
 * touches the floor meets its own reflection there.
 *
 * Call it AFTER everything that should reflect has been drawn (city, cell,
 * obstacles, fighters) and before anything that should not (the cell's front
 * frame, the HUD). Additive, so dark content contributes nothing and it can
 * only ever light the deck, never paint over what is already on it.
 *
 * @param {CanvasRenderingContext2D} ctx The frame being drawn.
 * @param {number} groundY Floor line, in canvas px.
 * @param {number} width  Canvas width.
 * @param {number} height Canvas height.
 */
export function drawDeckReflection(ctx, groundY, width, height) {
  const top = Math.round(groundY);
  const span = Math.min(height - top, top);
  if (span < 4) return;

  if (!gloss) gloss = document.createElement('canvas');
  if (gloss.width !== width || gloss.height < span) {
    gloss.width = width;
    gloss.height = span;
  }
  const g = gloss.getContext('2d');

  // Rows [top - span, top) of the frame, flipped into [0, span).
  g.globalCompositeOperation = 'copy';
  g.setTransform(1, 0, 0, -1, 0, span);
  g.drawImage(ctx.canvas, 0, top - span, width, span, 0, 0, width, span);
  g.setTransform(1, 0, 0, 1, 0, 0);

  // Strongest at the line, gone well before the bottom of the frame: a
  // rough-wet surface reflects near things clearly and far things not at all.
  g.globalCompositeOperation = 'destination-in';
  const fade = g.createLinearGradient(0, 0, 0, span);
  fade.addColorStop(0, 'rgba(0, 0, 0, 0.38)');
  fade.addColorStop(0.35, 'rgba(0, 0, 0, 0.14)');
  fade.addColorStop(1, 'rgba(0, 0, 0, 0)');
  g.fillStyle = fade;
  g.fillRect(0, 0, width, span);
  g.globalCompositeOperation = 'source-over';

  ctx.save();
  ctx.setTransform(1, 0, 0, 1, 0, 0);
  ctx.globalCompositeOperation = 'lighter';
  ctx.drawImage(gloss, 0, 0, width, span, 0, top, width, span);
  ctx.restore();
}
